import { AlertFilled, WarningOutlined } from '@ant-design/icons'
import { Alert } from 'antd'
import classNames from 'classnames'
import Link from 'next/link'
import { useIntl } from 'react-intl'

import urls from '@/config/uiUrls'
import { USAGE_ALERT_LIMITS } from '@/config/usage'
import { useThemeContext } from '@/context/ThemeContext'
import { useWorkspaceContext } from '@/context/WorkspaceContext'
import { useUsage } from '@/hooks/useUsage'
import {
  diffDays,
  nextFirstOfMonth,
  parseApiDateTimeTimezone,
} from '@/utils/datetime'
import messages, { buildI18nFunction } from '@/utils/messages'
import {
  calculateCumulativeRemainingAllowance,
  isReferralAllowanceQuota,
} from '@/utils/usage'

import { Workspace } from '@/types/index.d'

import styles from './UsageAlert.module.css'

const COMPONENT_ID = 'UsageAlert'

export function WorkspaceUsageAlert(): JSX.Element {
  const { workspace } = useWorkspaceContext()
  const intl = useIntl()
  const t = buildI18nFunction(intl.formatMessage)
  const dt = intl.formatDate
  const { data: usage, isLoading: isLoadingUsage } = useUsage(workspace?.id)

  const { isDarkMode } = useThemeContext()

  if (isLoadingUsage || !hasAlert(workspace)) {
    return null
  }

  if (workspace.status === 'TRIAL' && workspace?.trial.trial_expiration_date) {
    const now = new Date()
    const trialEndDate = parseApiDateTimeTimezone(
      workspace.trial.trial_expiration_date
    )

    const daysLeft = diffDays(trialEndDate, now)
    const hasRedCode = daysLeft <= 3

    const className = classNames(styles['alert'], {
      [styles['greencode']]: true,
      [styles['redcode']]: hasRedCode,
      [styles['alertDarkMode']]: isDarkMode,
    })

    return (
      <Alert
        banner
        showIcon={false}
        className={className}
        data-cy={COMPONENT_ID}
        description={t(
          `${COMPONENT_ID}.${'TRIAL'}${hasRedCode ? '.REDCODE' : ''}.description`,
          {
            date: dt(trialEndDate, { dateStyle: 'full' }),
            days: daysLeft,
          }
        )}
        message={t(
          `${COMPONENT_ID}.${'TRIAL'}${hasRedCode ? '.REDCODE' : ''}.message`,
          { days: daysLeft }
        )}
      />
    )
  }

  const { cumulative: remainingPrScans } =
    calculateCumulativeRemainingAllowance(usage, 'PR_SCAN')
  const { cumulative: remainingRevisionScans } =
    calculateCumulativeRemainingAllowance(usage, 'REVISION_SCAN')
  const remaining = remainingPrScans + remainingRevisionScans
  if (
    remainingPrScans <= USAGE_ALERT_LIMITS.WARNING ||
    remainingRevisionScans <= USAGE_ALERT_LIMITS.WARNING
  ) {
    const renewDateString = usage?.quotas?.filter(isReferralAllowanceQuota)?.[0]
      ?.renew_date
    const hasRedCode =
      (typeof remainingPrScans !== 'object' &&
        remainingPrScans <= USAGE_ALERT_LIMITS.CRITICAL) ||
      (typeof remainingRevisionScans !== 'object' &&
        remainingRevisionScans <= USAGE_ALERT_LIMITS.CRITICAL)

    return (
      <Alert
        banner
        showIcon={false}
        data-cy={COMPONENT_ID}
        className={classNames(styles['alert'], {
          [styles['greencode']]:
            remainingPrScans <= USAGE_ALERT_LIMITS.WARNING ||
            remainingRevisionScans <= USAGE_ALERT_LIMITS.WARNING,
          [styles['redcode']]: hasRedCode,
          [styles['alertDarkMode']]: isDarkMode,
        })}
        message={
          <>
            {<WarningOutlined />}{' '}
            {t(
              `${COMPONENT_ID}.${'FREEMIUM'}${hasRedCode ? '.REDCODE' : ''}.message`,
              {
                remaining,
                remainingPrScans,
                remainingRevisionScans,
              }
            )}
          </>
        }
        description={
          <>
            {t(
              `${COMPONENT_ID}.${'FREEMIUM'}${
                hasRedCode ? '.REDCODE' : ''
              }.description`,
              {
                date: dt(
                  renewDateString
                    ? new Date(renewDateString)
                    : nextFirstOfMonth(),
                  { dateStyle: 'full' }
                ),
                remaining,
                remainingPrScans,
                remainingRevisionScans,
              }
            )}{' '}
            {
              <Link href={urls.settings.usage}>
                {t(`${COMPONENT_ID}.learnmore`)}
              </Link>
            }
            .
          </>
        }
      />
    )
  }

  if (hasWorkspaceWarnings(workspace) && usage?.usage_status) {
    const className = classNames(styles['alert'], {
      [styles['greencode']]: usage.usage_status === 'USAGE_WARNING_ALERT',
      [styles['redcode']]: usage.usage_status === 'UPGRADE_REQUIRED',
      [styles['alertDarkMode']]: isDarkMode,
    })
    const exceedingQuotas = usage.quotas.filter((quota) => quota.exceeded)
    const icon =
      usage.usage_status === 'UPGRADE_REQUIRED' ? (
        <AlertFilled />
      ) : (
        <WarningOutlined />
      )
    const message = t(
      `${COMPONENT_ID}.${usage.usage_status}.message` as keyof typeof messages,
      {
        number: exceedingQuotas.length,
        type:
          exceedingQuotas.length === 1 ? exceedingQuotas[0].type : undefined,
      }
    )
    const description = t(
      `${COMPONENT_ID}.${usage.usage_status}.description` as keyof typeof messages,
      {
        date: dt(new Date(usage.usage_warning_alert_deadline_date), {
          dateStyle: 'full',
        }),
      }
    )
    return (
      <Alert
        banner
        showIcon={false}
        className={className}
        data-cy={COMPONENT_ID}
        message={
          <>
            {icon} {message}
          </>
        }
        description={
          <>
            {description}{' '}
            {
              <Link href={urls.settings.usage}>
                {t(`${COMPONENT_ID}.learnmore`)}
              </Link>
            }
            .
          </>
        }
      />
    )
  }

  return null
}

export function hasAlert(workspace: Workspace) {
  return (
    hasWorkspaceWarnings(workspace) ||
    workspace.status === 'FREEMIUM' ||
    workspace.status === 'TRIAL'
  )
}

function hasWorkspaceWarnings(workspace: Workspace) {
  return workspace?.warnings.find((warning) => warning.type === 'USAGE')?.active
}
